import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  jsonLd?: Record<string, any>;
}

export default function SEO({
  title,
  description,
  path,
  ogType = 'website',
  ogImage = 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?fm=webp&fit=crop&q=80&w=1200',
  jsonLd,
}: SEOProps) {
  const baseUrl = 'https://thelocalagora.com';
  const fullUrl = `${baseUrl}${path}`;

  useEffect(() => {
    // 1. Title
    document.title = title;

    // Helper to get or create element
    const getOrCreateMeta = (selector: string, attributeName: string, attributeValue: string) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      return element;
    };

    // 2. Meta Description
    const metaDesc = getOrCreateMeta('meta[name="description"]', 'name', 'description');
    metaDesc.setAttribute('content', description);

    // 3. Link Canonical
    let linkCanonical = document.head.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', fullUrl);

    // 4. Open Graph Tags
    const ogUrl = getOrCreateMeta('meta[property="og:url"]', 'property', 'og:url');
    ogUrl.setAttribute('content', fullUrl);

    const ogTitle = getOrCreateMeta('meta[property="og:title"]', 'property', 'og:title');
    ogTitle.setAttribute('content', title);

    const ogDesc = getOrCreateMeta('meta[property="og:description"]', 'property', 'og:description');
    ogDesc.setAttribute('content', description);

    const ogImg = getOrCreateMeta('meta[property="og:image"]', 'property', 'og:image');
    ogImg.setAttribute('content', ogImage);

    const ogTp = getOrCreateMeta('meta[property="og:type"]', 'property', 'og:type');
    ogTp.setAttribute('content', ogType);

    // 5. Twitter Card Tags
    const twCard = getOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card');
    twCard.setAttribute('content', 'summary_large_image');

    const twUrl = getOrCreateMeta('meta[name="twitter:url"]', 'name', 'twitter:url');
    twUrl.setAttribute('content', fullUrl);

    const twTitle = getOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title');
    twTitle.setAttribute('content', title);

    const twDesc = getOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description');
    twDesc.setAttribute('content', description);

    const twImg = getOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image');
    twImg.setAttribute('content', ogImage);

    // 6. JSON-LD scripts
    let sitemapScript = document.head.querySelector('script[type="application/ld+json"][id="seo-jsonld"]');
    if (jsonLd) {
      if (!sitemapScript) {
        sitemapScript = document.createElement('script');
        sitemapScript.setAttribute('type', 'application/ld+json');
        sitemapScript.setAttribute('id', 'seo-jsonld');
        document.head.appendChild(sitemapScript);
      }
      sitemapScript.textContent = JSON.stringify(jsonLd);
    } else {
      if (sitemapScript) {
        document.head.removeChild(sitemapScript);
      }
    }

    // Cleanup logic to not clutter the head on unmount or navigation
    return () => {
      // We can leave titles & default descriptions intact for performance
    };
  }, [title, description, fullUrl, ogType, ogImage, jsonLd]);

  return null;
}
